import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AnimatePresence } from "framer-motion";
import { LogOut, Download } from "lucide-react";
import { Logo } from "../components/Logo";
import { LeadDrawer } from "../components/LeadDrawer";
import { LeadsChart } from "../components/LeadsChart";
import { KpiCards } from "../components/admin/KpiCards";
import { LeadsFilterBar } from "../components/admin/LeadsFilterBar";
import { LeadsTable } from "../components/admin/LeadsTable";
import { useAuthGuard } from "../hooks/useAuthGuard";
import { useLeadsData } from "../hooks/useLeadsData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const matchesQuery = (lead, query) =>
  [lead.full_name, lead.business_name, lead.email, lead.phone, lead.loan_amount]
    .some((v) => v && v.toLowerCase().includes(query));

export default function PackagerPortal() {
  const user = useAuthGuard("packager");
  const nav = useNavigate();
  const { leads, stats, caseManagers, setCaseManagers, load, changeStatus, changeCaseManager } = useLeadsData(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [selectedId, setSelectedId] = useState(null);

  const filtered = useMemo(() => {
    if (!leads) return null;
    const q = query.trim().toLowerCase();
    return leads.filter((l) => {
      if (statusFilter !== "All" && l.status !== statusFilter) return false;
      if (!q) return true;
      return matchesQuery(l, q);
    });
  }, [leads, query, statusFilter]);

  const selectedLead = leads?.find((l) => l.id === selectedId) || null;

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    nav("/admin/login");
  };

  const exportCsv = async () => {
    const res = await axios.get(`${API}/leads/export`, { withCredentials: true, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `my-cases-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6]" data-testid="packager-portal">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-[#E5E7EB]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            {user && <span className="text-sm font-semibold text-[#052F24]/70">{user.name}</span>}
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="flex items-center gap-2 text-sm font-semibold text-[#052F24]/70 hover:text-[#052F24] transition-colors"
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">My cases</h1>
          <button
            onClick={exportCsv}
            disabled={!leads || leads.length === 0}
            data-testid="export-csv-btn"
            className="flex items-center gap-2 rounded-full bg-[#052F24] text-[#DFFF00] px-5 py-2.5 text-sm font-bold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download size={16} /> Export CSV
          </button>
        </div>

        <KpiCards stats={stats} />

        <LeadsChart leads={leads} />

        <LeadsFilterBar
          query={query}
          onQueryChange={setQuery}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          packagerFilter=""
          onClearPackagerFilter={() => {}}
        />

        <LeadsTable
          leads={leads}
          filtered={filtered}
          caseManagers={caseManagers}
          viewerRole="packager"
          onSelect={setSelectedId}
          onStatusChange={changeStatus}
          onCaseManagerChange={changeCaseManager}
          onCaseManagersOptionsChange={setCaseManagers}
        />
      </main>

      <AnimatePresence>
        {selectedLead && (
          <LeadDrawer
            lead={selectedLead}
            onClose={() => setSelectedId(null)}
            onStatusChange={changeStatus}
            onRefresh={load}
            caseManagers={caseManagers}
            onCaseManagerChange={changeCaseManager}
            onCaseManagersOptionsChange={setCaseManagers}
            viewerRole="packager"
          />
        )}
      </AnimatePresence>
    </div>
  );
}
